/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Report/History download buttons injected into an ag-grid pagination footer.
 *
 * Extracted from the aggregator grids so the identical button DOM + disabled-state
 * logic lives in one place instead of being copy-pasted per component. The owning
 * component supplies the export callbacks and the enable/disable predicates, and
 * delegates the actual DOM insertion (which needs the grid's afterNextRender /
 * pagination-panel plumbing) back through the {@link ensureInFooter} `insert` arg.
 */
export interface GridDownloadButtonsConfig {
    canExportReport: () => boolean;
    canExportHistory: () => boolean;
    onExportReport: () => void;
    onExportHistory: () => void;
}

export class GridDownloadButtons {
    private container: HTMLElement | null = null;
    private reportButton: HTMLButtonElement | null = null;
    private historyButton: HTMLButtonElement | null = null;

    constructor(private readonly config: GridDownloadButtonsConfig) {
    }

    get isConnected(): boolean {
        return !!this.container?.isConnected;
    }

    ensureInFooter(gridHost: HTMLElement | null | undefined,
                   insert: (node: Node, gridHost: HTMLElement) => void): void {
        if (this.isConnected || !gridHost) {
            return;
        }
        if (!this.container) {
            this.container = this.buildContainer();
        }
        insert(this.container, gridHost);
    }

    updateDisabledState(): void {
        if (this.reportButton) {
            this.reportButton.disabled = !this.config.canExportReport();
        }
        if (this.historyButton) {
            this.historyButton.disabled = !this.config.canExportHistory();
        }
    }

    private buildContainer(): HTMLElement {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'flex-start';
        container.style.flex = '1';

        this.reportButton = this.createButton('Download Report', 'Download report', () => this.config.onExportReport());
        this.historyButton = this.createButton('Download History', 'Download history', () => this.config.onExportHistory());
        this.historyButton.style.marginLeft = '5px';

        container.appendChild(this.reportButton);
        container.appendChild(this.historyButton);
        return container;
    }

    private createButton(label: string, title: string, onClick: () => void): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = label;
        btn.title = title;
        btn.className = 'ag-grid-add-row-pagination-btn';
        btn.addEventListener('click', () => onClick());
        return btn;
    }
}
